import React, { useState } from "react";
import { userApi } from "../api/routes";
import { Button, Card, Input, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const emptyUser = {
  userId: "",
  authProviderId: "",
  email: "",
  phone: "",
  name: "",
  planTier: "",
  smsLimits: 0,
  usageCounter: 0,
  accountStatus: "",
  onboardingStatus: "",
  accessStatus: "",
  billingSource: "",
  role: "",
  preferredDistance: "",
  timeZone: "",
};

export default function UsersPanel() {
  const [emailLookup, setEmailLookup] = useState("");
  const [idLookup, setIdLookup] = useState("");
  const [user, setUser] = useState(emptyUser);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("search");
  const [toast, setToast] = useState(null);
  const [panelState, setPanelState] = usePanelCache("users");
  const results = panelState.results || [];
  const showSearch = panelState.showSearch ?? true;

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  };

  const resetToSearch = () => {
    setMode("search");
    setUser(emptyUser);
  };

  const handleGetByEmail = async () => {
    if (!emailLookup.trim()) {
      showToast("Enter an email before searching", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await userApi.byEmail(emailLookup);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      showToast(e.displayMessage || e.message || "Error searching user", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGetById = async () => {
    if (!idLookup.trim()) {
      showToast("Enter a user ID before searching", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await userApi.byId(idLookup);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      showToast(e.displayMessage || e.message || "Error searching user", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const res = await userApi.create(user);
      const created = res.data || user;
      setPanelState({ results: [created, ...(results || [])] });
      resetToSearch();
      showToast("User created");
    } catch (e) {
      showToast(e.displayMessage || e.message || "Error creating user", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!user.userId) {
      showToast("userId required to update", "error");
      return;
    }
    setLoading(true);
    try {
      const res = await userApi.update(user.userId, user);
      const updated = res.data || user;
      setPanelState({
        results: (results || []).map((u) => (u.userId === updated.userId ? { ...u, ...updated } : u)),
      });
      resetToSearch();
      showToast("User updated");
    } catch (e) {
      showToast(e.displayMessage || e.message || "Error updating user", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderTable = () => (
    <Card
      title="Users"
      action={
        <div style={{ display: "flex", gap: 8 }}>
          <Button tone="ghost" onClick={() => setPanelState({ results: [] })}>Clear</Button>
          <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>{showSearch ? "Hide search" : "Show search"}</Button>
          <Button onClick={() => { setUser(emptyUser); setMode("edit"); }}>+ New User</Button>
        </div>
      }
    >
      {showSearch && (
        <Card title="Search">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
            <Input label="Email" value={emailLookup} onChange={setEmailLookup} placeholder="user@example.com" />
            <Input label="User ID" value={idLookup} onChange={setIdLookup} placeholder="uuid" />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button onClick={handleGetByEmail} disabled={!emailLookup.trim()} loading={loading}>Search by Email</Button>
            <Button tone="secondary" onClick={handleGetById} disabled={!idLookup.trim()} loading={loading}>Search by ID</Button>
          </div>
        </Card>
      )}

      {results.length === 0 ? (
        <div style={{ padding: 12, color: "var(--stone-700)" }}>No results yet. Run a search.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
          <thead>
            <tr style={{ background: "rgba(25,164,99,0.08)" }}>
              <th style={{ padding: 8, textAlign: "left" }}>Name</th>
              <th style={{ padding: 8, textAlign: "left" }}>Email</th>
              <th style={{ padding: 8, textAlign: "left" }}>Role</th>
              <th style={{ padding: 8, textAlign: "left" }}>Access</th>
              <th style={{ padding: 8, textAlign: "left" }}>User ID</th>
            </tr>
          </thead>
          <tbody>
            {results.map((u) => (
              <tr
                key={u.userId || u.email}
                style={{ borderBottom: "1px solid rgba(15,23,42,0.06)", cursor: "pointer" }}
                onClick={() => { setUser({ ...emptyUser, ...u }); setMode("edit"); }}
              >
                <td style={{ padding: 8 }}>{u.name}</td>
                <td style={{ padding: 8 }}>{u.email}</td>
                <td style={{ padding: 8 }}>{u.role}</td>
                <td style={{ padding: 8 }}>{u.accessStatus || u.accountStatus}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{u.userId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );

  const renderEdit = () => (
    <Card
      title={user.userId ? "Edit user" : "Create user"}
      action={<Button tone="ghost" onClick={resetToSearch}>← Back to search</Button>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 12 }}>
        <Input label="Name" value={user.name} onChange={(v) => setUser({ ...user, name: v })} />
        <Input label="Email" value={user.email} onChange={(v) => setUser({ ...user, email: v })} />
        <Input label="Phone" value={user.phone} onChange={(v) => setUser({ ...user, phone: v })} />
        <Input label="Role" value={user.role} onChange={(v) => setUser({ ...user, role: v })} />
        <Input label="Plan Tier" value={user.planTier} onChange={(v) => setUser({ ...user, planTier: v })} />
        <Input
          label="Preferred Distance"
          value={user.preferredDistance}
          onChange={(v) => setUser({ ...user, preferredDistance: v })}
        />
        <Input label="Time Zone" value={user.timeZone} onChange={(v) => setUser({ ...user, timeZone: v })} />
        <Input
          label="Account Status"
          value={user.accountStatus}
          onChange={(v) => setUser({ ...user, accountStatus: v })}
        />
        <Input
          label="Onboarding Status"
          value={user.onboardingStatus}
          onChange={(v) => setUser({ ...user, onboardingStatus: v })}
        />
        <Input
          label="Access Status"
          value={user.accessStatus}
          onChange={(v) => setUser({ ...user, accessStatus: v })}
        />
        {user.userId && (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>User ID</div>
            <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(15,23,42,0.06)", fontFamily: "monospace", fontWeight: 700 }}>
              {user.userId}
            </div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        {!user.userId && (
          <Button onClick={handleCreate} loading={loading}>Create</Button>
        )}
        {user.userId && (
          <Button tone="secondary" onClick={handleUpdate} loading={loading}>Update</Button>
        )}
      </div>
    </Card>
  );

  return (
    <>
      {mode === "search" ? renderTable() : renderEdit()}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
